package com.ijoin.ui.payment

import com.ijoin.config.BookingSettings
import com.ijoin.data.model.CreatePaymentIntentRequest
import com.ijoin.data.repository.BookingRepository
import com.ijoin.data.repository.PaymentRepository
import com.ijoin.data.repository.TicketRepository
import com.ijoin.i18n.globalStrings
import com.ijoin.platform.getDefaultPaymentReturnUrl
import kotlinx.coroutines.*
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update

data class PaymentUiState(
    val isLoading: Boolean = true,
    val error: String? = null,
    val clientSecret: String? = null,
    val publishableKey: String? = null,
    val paymentIntentId: String? = null,
    val paymentId: String? = null,
    val paymentCompleted: Boolean = false,
    val isFetchingTickets: Boolean = false,
    val ticketFetchError: String? = null,
    val issuedTicketId: String? = null,
)

private const val TICKET_FETCH_DELAY_MS = 2000L

class PaymentViewModel(
    private val args: PaymentFlowArgs,
    private val paymentRepository: PaymentRepository = PaymentRepository(),
    private val bookingRepository: BookingRepository = BookingRepository(),
    private val ticketRepository: TicketRepository = TicketRepository(),
) {
    private val strings
        get() = globalStrings()
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Main)
    private var heartbeatJob: Job? = null
    private var ticketFetchJob: Job? = null
    private var backendPaymentId: String? = null

    private val _uiState = MutableStateFlow(PaymentUiState())
    val uiState: StateFlow<PaymentUiState> = _uiState.asStateFlow()

    init {
        createPaymentIntent()
        startReservationHeartbeat()
    }

    fun retry() {
        createPaymentIntent()
    }

    fun handleStripeSuccess() {
        _uiState.update { state ->
            state.copy(
                paymentCompleted = true,
                error = null,
                ticketFetchError = null,
            )
        }
        stopReservationHeartbeat()
        fetchTicketsAfterPayment()
    }

    fun handleStripeError(message: String) {
        _uiState.update { state ->
            state.copy(
                error = message,
            )
        }
    }

    fun dispose() {
        stopReservationHeartbeat()
        ticketFetchJob?.cancel()
        scope.cancel()
    }

    private fun createPaymentIntent() {
        scope.launch {
            backendPaymentId = null
            _uiState.value = PaymentUiState(isLoading = true)
            val request = CreatePaymentIntentRequest(
                eventId = args.eventId,
                quantity = args.quantity,
                priceTierName = args.priceTierName,
                overrideAmount = args.overrideAmount,
                currency = args.currency,
                bookingId = args.bookingId,
                occurrenceDateTime = args.occurrenceDateTime,
                successUrl = args.successUrl ?: getDefaultPaymentReturnUrl(),
                cancelUrl = args.cancelUrl ?: getDefaultPaymentReturnUrl(),
            )

            val result = paymentRepository.createPaymentIntent(request)
            result
                .onSuccess { response ->
                    backendPaymentId = response.paymentId
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            error = null,
                            clientSecret = response.clientSecret,
                            publishableKey = response.publishableKey,
                            paymentIntentId = response.paymentIntentId,
                            paymentId = response.paymentId,
                            paymentCompleted = false,
                            ticketFetchError = null,
                            issuedTicketId = null,
                        )
                    }
                }
                .onFailure { throwable ->
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            error = throwable.message ?: strings.paymentStartFailed,
                            clientSecret = null,
                            publishableKey = null,
                            paymentId = null,
                            ticketFetchError = null,
                            issuedTicketId = null,
                        )
                    }
                }
        }
    }

    private fun startReservationHeartbeat() {
        val bookingId = args.bookingId ?: return
        if (heartbeatJob?.isActive == true) return
        heartbeatJob = scope.launch {
            while (isActive && !uiState.value.paymentCompleted) {
                bookingRepository.pingReservation(bookingId)
                delay(BookingSettings.RESERVATION_PING_INTERVAL_MS)
            }
        }
    }

    private fun stopReservationHeartbeat() {
        heartbeatJob?.cancel()
        heartbeatJob = null
    }

    private fun fetchTicketsAfterPayment(force: Boolean = false) {
        if (ticketFetchJob?.isActive == true) {
            if (!force) {
                return
            }
            ticketFetchJob?.cancel()
        }
        val bookingId = args.bookingId
        val paymentId = backendPaymentId
        if (bookingId == null && paymentId == null) {
            _uiState.update {
                it.copy(
                    ticketFetchError = strings.paymentTicketContextMissing
                )
            }
            return
        }
        ticketFetchJob = scope.launch {
            val shouldDelay = !force
            _uiState.update {
                it.copy(
                    isFetchingTickets = true,
                    ticketFetchError = null
                )
            }
            if (shouldDelay) {
                delay(TICKET_FETCH_DELAY_MS)
            }
            val result = if (bookingId != null) {
                ticketRepository.getTicketsByBooking(bookingId)
            } else {
                ticketRepository.getTicketsByPayment(paymentId!!)
            }
            result
                .onSuccess { tickets ->
                    val ticketId = tickets.firstOrNull()?.id
                    _uiState.update {
                        it.copy(
                            isFetchingTickets = false,
                            ticketFetchError = if (ticketId == null) strings.paymentTicketsNotFound else null,
                            issuedTicketId = ticketId
                        )
                    }
                }
                .onFailure { error ->
                    _uiState.update {
                        it.copy(
                            isFetchingTickets = false,
                            ticketFetchError = error.message ?: strings.paymentTicketsFetchFailed
                        )
                    }
                }
        }
    }

    fun retryFetchTickets() {
        if (_uiState.value.paymentCompleted) {
            fetchTicketsAfterPayment(force = true)
        }
    }

    fun onTicketHandled() {
        _uiState.update { it.copy(issuedTicketId = null) }
    }
}
